import React from 'react';
import { LinkOutlined } from '@ant-design/icons';
import { Button, Text, TextAs, TextSizes, Tooltip, ButtonHierarchies } from '@clockout-dev/design-elements';
import SectionCard from '../../common/sectionCard/SectionCard';
import IssueStatusPill from '../../common/issueStatusPill/IssueStatusPill';
import IssueTypeTag from '../../common/issueTypeTag/IssueTypeTag';
import { TICKET_JIRA_LINK_COPY, issueSummary } from '../issueTracker.constants';
import styles from '../styles/ticket-jira-link.module.scss';

/**
 * The Jira issue(s) this support ticket produced, in the ticket detail sidebar.
 *
 * READ-ONLY by design. Linking and unlinking happen in clockout-staff; the FI
 * sees the relationship and the current status, and has no controls. There is
 * also no Jira URL — the FI has no Jira account.
 *
 * `available: false` is the case worth being careful about: the mirrored issue
 * stopped being publishable to this FI (label removed, moved project, deleted).
 * The row STAYS, with the key, marked unavailable — dropping it would silently
 * rewrite the ticket's history, and the key is what makes the trail traceable.
 *
 * The server also sends `stage` and `linkedAt` on each link. Neither is drawn
 * here: `stage` is the fine-grained second level, which belongs in the detail
 * modal where there is room to label it, and `linkedAt` is when STAFF attached
 * the issue — an internal bookkeeping moment the FI did not witness and cannot
 * act on. Both are one click away in the modal.
 *
 * @param {Object} props
 * @param {Array<{key: string, summary: string, issueType: string, status: string,
 *                stage: string, available: boolean, linkedAt: string}>} [props.jiraLinks]
 * @param {(key: string) => void} [props.onOpenIssue] opens the issue detail modal
 * @param {(statusKey: string) => object|undefined} [props.resolveStatusEntry]
 */
function TicketJiraLink({ jiraLinks, onOpenIssue, resolveStatusEntry }) {
    if (!Array.isArray(jiraLinks) || jiraLinks.length === 0) return null;

    return (
        <SectionCard icon={<LinkOutlined />} title={TICKET_JIRA_LINK_COPY.TITLE}>
            <div className={styles.list}>
                {jiraLinks.map((link) => (
                    <div key={link.key} className={styles.item}>
                        <div className={styles.header}>
                            {/* The key opens the full issue — the same modal the
                                Jira tab uses. A link carries only the four fields
                                below, so everything else is fetched there. An
                                unavailable issue is not clickable: there is no
                                detail left to show. */}
                            {link.available === false ? (
                                <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.key}>
                                    {link.key}
                                </Text>
                            ) : (
                                <Button
                                    hierarchy={ButtonHierarchies.LINK_COLOR}
                                    onClick={() => onOpenIssue?.(link.key)}
                                >
                                    {link.key}
                                </Button>
                            )}
                            <IssueTypeTag type={link.issueType} />
                            {link.available === false ? (
                                <Tooltip title={TICKET_JIRA_LINK_COPY.UNAVAILABLE_HINT} placement="top">
                                    <Text
                                        as={TextAs.SPAN}
                                        size={TextSizes.EXTRA_SMALL}
                                        role="status"
                                        className={styles.unavailable}
                                    >
                                        {TICKET_JIRA_LINK_COPY.UNAVAILABLE}
                                    </Text>
                                </Tooltip>
                            ) : (
                                <IssueStatusPill status={link.status} servedEntry={resolveStatusEntry?.(link.status)} />
                            )}
                        </div>
                        {/* Always rendered. A bare key tells an FI nothing about
                            what is being worked on, which is the whole point of
                            showing the link. */}
                        <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.summary}>
                            {issueSummary(link.summary)}
                        </Text>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

export default TicketJiraLink;
